import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  ArrowRight, ArrowLeft, X, Sparkles, LayoutDashboard,
  Calendar, FileText, BarChart3, BookOpen, Rocket, PartyPopper
} from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'welcome',
    target: null,
    title: 'Welcome to StelliSocial',
    description: 'Let\'s take a quick tour to help you get the most out of your social media management platform. This will only take a minute.',
    icon: Sparkles,
    position: 'center',
  },
  {
    id: 'sidebar',
    target: '[data-tour="sidebar-nav"]',
    title: 'Navigation Sidebar',
    description: 'This is your main navigation hub. Access all features from here -- Calendars, Posts, Analytics, and more. Everything is one click away.',
    icon: LayoutDashboard,
    position: 'right',
  },
  {
    id: 'create-post',
    target: '[data-tour="create-post"]',
    title: 'Create Your First Post',
    description: 'This is where the magic happens. Click here to open the Post Composer where you can write content, select platforms, use AI writing tools, and schedule posts.',
    icon: FileText,
    position: 'right',
  },
  {
    id: 'stats',
    target: '[data-tour="stats-grid"]',
    title: 'Performance at a Glance',
    description: 'Your dashboard stats show total posts, scheduled content, published items, and engagement metrics -- all updated in real time with trend indicators.',
    icon: BarChart3,
    position: 'bottom',
  },
  {
    id: 'calendars',
    target: '[data-tour="nav-calendars"]',
    title: 'Content Calendars',
    description: 'Organize content by brand, client, or campaign. Create calendars, schedule posts on a visual grid, and even use AI to auto-fill your content plan for the week.',
    icon: Calendar,
    position: 'right',
  },
  {
    id: 'content-ideas',
    target: '[data-tour="nav-content-ideas"]',
    title: 'AI Content Ideas',
    description: 'Stuck for inspiration? Let AI generate content ideas based on your topic, industry, and target platforms. Save favorites and convert them to posts instantly.',
    icon: Sparkles,
    position: 'right',
  },
  {
    id: 'analytics',
    target: '[data-tour="nav-analytics"]',
    title: 'Analytics & Insights',
    description: 'Track reach, engagement, clicks, and audience demographics across all platforms. Find the best times to post and export reports for stakeholders.',
    icon: BarChart3,
    position: 'right',
  },
  {
    id: 'user-guide',
    target: '[data-tour="nav-user-guide"]',
    title: 'User Guide',
    description: 'Need help anytime? The User Guide has detailed walkthroughs with screenshots for every feature. It\'s your go-to reference.',
    icon: BookOpen,
    position: 'right',
  },
  {
    id: 'complete',
    target: null,
    title: 'You\'re All Set!',
    description: 'You now know the essentials. Start by creating a Calendar, then compose your first post. You can always restart this tour from Settings.',
    icon: PartyPopper,
    position: 'center',
  },
];

function getTooltipStyle(targetRect, position, tooltipRef) {
  if (!targetRect || position === 'center') {
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  const tooltipWidth = 380;
  const tooltipHeight = tooltipRef?.current?.offsetHeight || 200;
  const gap = 16;
  const padding = 20;

  let top, left;

  switch (position) {
    case 'right':
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      left = targetRect.right + gap;
      if (left + tooltipWidth > window.innerWidth - padding) {
        left = targetRect.left - tooltipWidth - gap;
      }
      break;
    case 'bottom':
      top = targetRect.bottom + gap;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      if (top + tooltipHeight > window.innerHeight - padding) {
        top = targetRect.top - tooltipHeight - gap;
      }
      break;
    case 'left':
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      left = targetRect.left - tooltipWidth - gap;
      if (left < padding) {
        left = targetRect.right + gap;
      }
      break;
    case 'top':
      top = targetRect.top - tooltipHeight - gap;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      break;
    default:
      top = targetRect.bottom + gap;
      left = targetRect.left;
  }

  top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
  left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));

  return {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
  };
}

export default function OnboardingTour({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef(null);
  const navigate = useNavigate();

  const step = TOUR_STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === TOUR_STEPS.length - 1;
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  const updateTargetRect = useCallback(() => {
    if (!step.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top - 6,
        left: rect.left - 6,
        width: rect.width + 12,
        height: rect.height + 12,
        right: rect.right + 6,
        bottom: rect.bottom + 6,
      });
    } else {
      setTargetRect(null);
    }
  }, [step.target]);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setIsAnimating(true);
    updateTargetRect();
    const timer = setTimeout(() => setIsAnimating(false), 300);
    return () => clearTimeout(timer);
  }, [currentStep, updateTargetRect]);

  useEffect(() => {
    window.addEventListener('resize', updateTargetRect);
    window.addEventListener('scroll', updateTargetRect, true);
    return () => {
      window.removeEventListener('resize', updateTargetRect);
      window.removeEventListener('scroll', updateTargetRect, true);
    };
  }, [updateTargetRect]);

  const goNext = () => {
    if (isLast) {
      completeTour();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const goBack = () => {
    if (!isFirst) setCurrentStep(prev => prev - 1);
  };

  const completeTour = () => {
    localStorage.setItem('stellisocial_tour_completed', 'true');
    onComplete?.();
  };

  const skipTour = () => {
    localStorage.setItem('stellisocial_tour_completed', 'true');
    onComplete?.();
  };

  if (!visible) return null;

  const Icon = step.icon;
  const tooltipStyle = getTooltipStyle(targetRect, step.position, tooltipRef);

  const spotlightPath = targetRect
    ? `M 0 0 H ${window.innerWidth} V ${window.innerHeight} H 0 Z M ${targetRect.left} ${targetRect.top} V ${targetRect.top + targetRect.height} H ${targetRect.left + targetRect.width} V ${targetRect.top} Z`
    : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000]"
      data-testid="onboarding-tour"
      style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease' }}
    >
      {/* Overlay */}
      {targetRect ? (
        <svg
          className="fixed inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 10000 }}
        >
          <defs>
            <mask id="tour-spotlight">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.65)"
            mask="url(#tour-spotlight)"
          />
          <rect
            x={targetRect.left - 2}
            y={targetRect.top - 2}
            width={targetRect.width + 4}
            height={targetRect.height + 4}
            rx="10"
            fill="none"
            stroke="rgba(52,211,153,0.7)"
            strokeWidth="2"
            className="animate-pulse"
          />
        </svg>
      ) : (
        <div className="fixed inset-0 bg-black/65" style={{ zIndex: 10000 }} />
      )}

      {/* Click blocker */}
      <div
        className="fixed inset-0"
        style={{ zIndex: 10001 }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`w-[380px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ${
          isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        }`}
        style={{ ...tooltipStyle, zIndex: 10002 }}
        data-testid="tour-tooltip"
      >
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div
            className="h-full bg-emerald-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="p-6">
          {/* Icon + Step counter */}
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Icon className="h-5 w-5 text-emerald-600" />
            </div>
            <span className="text-xs font-medium text-slate-400">
              {currentStep + 1} of {TOUR_STEPS.length}
            </span>
          </div>

          {/* Content */}
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-testid="tour-step-title">
            {step.title}
          </h3>
          <p className="text-sm text-slate-500 leading-relaxed mb-6">
            {step.description}
          </p>

          {/* Dot indicators */}
          <div className="flex items-center justify-center gap-1.5 mb-5">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={`rounded-full transition-all duration-300 ${
                  i === currentStep
                    ? 'w-6 h-2 bg-emerald-500'
                    : i < currentStep
                    ? 'w-2 h-2 bg-emerald-300'
                    : 'w-2 h-2 bg-slate-200'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={skipTour}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="tour-skip-btn"
            >
              {isLast ? '' : 'Skip tour'}
            </button>
            <div className="flex items-center gap-2">
              {!isFirst && (
                <button
                  onClick={goBack}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid="tour-back-btn"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              )}
              <button
                onClick={goNext}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                  isLast
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25'
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
                data-testid="tour-next-btn"
              >
                {isLast ? (
                  <>
                    Get Started
                    <Rocket className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
